/**
 * 요소 선택 시스템
 * iframe 내 HTML 요소를 선택하고 편집 가능한 섹션을 식별
 */

// 편집 가능한 요소 타입 정의
export const EDITABLE_ELEMENT_TYPES = {
  VOCABULARY: 'vocabulary',
  QUESTION: 'question', 
  PASSAGE: 'passage',
  AUDIO: 'audio',
  STYLE: 'style'
};

// 간단한 특별 클래스 기반 선택 규칙
const ELEMENT_DETECTION_RULES = {
  [EDITABLE_ELEMENT_TYPES.VOCABULARY]: [
    '.editable-vocabulary'
  ],
  [EDITABLE_ELEMENT_TYPES.QUESTION]: [
    '.editable-question'
  ],
  [EDITABLE_ELEMENT_TYPES.PASSAGE]: [
    '.editable-passage'
  ],
  [EDITABLE_ELEMENT_TYPES.AUDIO]: [
    '.editable-audio'
  ],
  // 스타일 4개 박스
  'style-background': [
    '.editable-style-background'
  ],
  'style-card': [
    '.editable-style-card'
  ],
  'style-font': [
    '.editable-style-font'
  ],
  'style-cardsize': [
    '.editable-style-cardsize'
  ]
};

// iframe에 선택 기능 주입
export const injectSelectionHandler = (iframe, onElementSelect) => {
  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    if (!iframeDoc) {
      console.warn('iframe 문서에 접근할 수 없습니다.');
      return;
    }

    // 기존 선택 이벤트 정리
    cleanupSelectionEvents(iframeDoc);
    
    // 학생 정보 입력 자동 스킵
    autoSkipStudentInput(iframeDoc);
    
    // CSS 스타일 주입 (하이라이트 효과)
    injectHighlightStyles(iframeDoc);
    
    // 클릭 이벤트 리스너 추가
    const clickHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      const element = event.target;
      const elementInfo = analyzeElement(element, iframeDoc);
      
      console.log('🔍 클릭된 요소:', {
        clicked: element.tagName + (element.className ? '.' + element.className.split(' ')[0] : ''),
        isEditable: elementInfo.isEditable,
        selectedElement: elementInfo.element ? elementInfo.element.tagName + (elementInfo.element.className ? '.' + elementInfo.element.className.split(' ')[0] : '') : 'none',
        editType: elementInfo.editType,
        section: elementInfo.section
      });
      
      if (elementInfo.isEditable) {
        highlightElement(elementInfo.element, iframeDoc);
        onElementSelect(elementInfo);
      }
    };
    
    // 호버 이벤트 리스너 추가
    const hoverHandler = (event) => {
      const element = event.target;
      const elementInfo = analyzeElement(element, iframeDoc);
      
      if (elementInfo.isEditable) {
        addHoverEffect(elementInfo.element);
      }
    };
    
    const hoverOutHandler = (event) => {
      removeHoverEffect(event.target);
    };
    
    // 이벤트 등록
    iframeDoc.addEventListener('click', clickHandler, true);
    iframeDoc.addEventListener('mouseover', hoverHandler, true);
    iframeDoc.addEventListener('mouseout', hoverOutHandler, true);
    
    // 정리 함수 저장
    iframe._selectionCleanup = () => {
      iframeDoc.removeEventListener('click', clickHandler, true);
      iframeDoc.removeEventListener('mouseover', hoverHandler, true);
      iframeDoc.removeEventListener('mouseout', hoverOutHandler, true);
      cleanupSelectionEvents(iframeDoc);
    };
    
    console.log('✅ 요소 선택 핸들러 주입 완료');
    
  } catch (error) {
    console.error('❌ 선택 핸들러 주입 실패:', error);
  }
};

// 기존 선택 이벤트 정리
const cleanupSelectionEvents = (doc) => {
  // 기존 하이라이트 제거
  const existingHighlights = doc.querySelectorAll('.element-highlighted, .element-hover');
  existingHighlights.forEach(el => {
    el.classList.remove('element-highlighted', 'element-hover');
  });
  
  // 기존 스타일 태그 제거
  const existingStyles = doc.querySelectorAll('style[data-selector-styles]');
  existingStyles.forEach(style => style.remove());
};

// 하이라이트 CSS 스타일 주입
const injectHighlightStyles = (doc) => {
  const style = doc.createElement('style');
  style.setAttribute('data-selector-styles', 'true');
  style.textContent = `
    .element-hover {
      outline: 2px dashed #3b82f6 !important;
      outline-offset: 2px !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
    }
    
    .element-highlighted {
      outline: 3px solid #10b981 !important;
      outline-offset: 2px !important;
      background-color: rgba(16, 185, 129, 0.1) !important;
      box-shadow: 0 0 10px rgba(16, 185, 129, 0.3) !important;
    }
    
  `;
  doc.head.appendChild(style);
};

// 요소 분석 함수 - 간단한 특별 클래스 기반
const analyzeElement = (element, doc) => {
  const elementInfo = {
    element: element,
    tagName: element.tagName.toLowerCase(),
    className: element.className || '',
    id: element.id || '',
    textContent: element.textContent?.trim() || '',
    isEditable: false,
    editType: null,
    section: null,
    xpath: getXPath(element),
    boundingRect: element.getBoundingClientRect()
  };
  
  // 직접 매칭 또는 상위 요소에서 특별 클래스 검색
  for (const [type, selectors] of Object.entries(ELEMENT_DETECTION_RULES)) {
    for (const selector of selectors) {
      // 직접 매칭
      if (element.matches && element.matches(selector)) {
        elementInfo.isEditable = true;
        elementInfo.editType = type;
        elementInfo.section = detectSection(element, type);
        elementInfo.element = element;
        
        // 스타일 세부 타입 처리
        if (type.startsWith('style-')) {
          elementInfo.editType = EDITABLE_ELEMENT_TYPES.STYLE;
          elementInfo.styleSection = type;
          elementInfo.section = type.replace('style-', '');
        }
        return elementInfo;
      }
      
      // 상위 요소에서 찾기
      const parentElement = element.closest(selector);
      if (parentElement) {
        elementInfo.isEditable = true;
        elementInfo.editType = type;
        elementInfo.section = detectSection(parentElement, type);
        elementInfo.element = parentElement; // 상위 요소를 실제 선택 요소로 설정
        
        // 스타일 세부 타입 처리
        if (type.startsWith('style-')) {
          elementInfo.editType = EDITABLE_ELEMENT_TYPES.STYLE;
          elementInfo.styleSection = type;
          elementInfo.section = type.replace('style-', '');
        }
        return elementInfo;
      }
    }
  }
  
  return elementInfo;
};

// 섹션 감지 함수
const detectSection = (element, type) => {
  switch (type) {
    case EDITABLE_ELEMENT_TYPES.VOCABULARY:
      return 'vocabulary';
      
    case EDITABLE_ELEMENT_TYPES.QUESTION:
      if (element.closest('.quiz-item, .question')) {
        const questionIndex = Array.from(
          element.closest('body').querySelectorAll('.quiz-item, .question')
        ).indexOf(element.closest('.quiz-item, .question'));
        return `question-${questionIndex}`;
      }
      return 'questions-list';
      
    case EDITABLE_ELEMENT_TYPES.PASSAGE:
      if (element.matches('h1, h2, h3, .title')) {
        return 'passage-title';
      }
      return 'passage-content';
      
    case EDITABLE_ELEMENT_TYPES.AUDIO:
      return 'audio-controls';
      
    case EDITABLE_ELEMENT_TYPES.STYLE:
      return 'design-settings';
      
    // 스타일 세부 타입들
    case 'style-background':
      return 'background';
    case 'style-card':
      return 'card';
    case 'style-font':
      return 'font';
    case 'style-cardsize':
      return 'cardsize';
      
    default:
      return 'unknown';
  }
};

// XPath 생성
const getXPath = (element) => {
  if (element.id) {
    return `//*[@id="${element.id}"]`;
  }
  
  const parts = [];
  while (element && element.nodeType === Node.ELEMENT_NODE) {
    let tagName = element.nodeName.toLowerCase();
    let sibling = element;
    let siblingIndex = 1;
    
    while (sibling = sibling.previousElementSibling) {
      if (sibling.nodeName.toLowerCase() === tagName) {
        siblingIndex++;
      }
    }
    
    if (siblingIndex > 1) {
      tagName += `[${siblingIndex}]`;
    }
    
    parts.unshift(tagName);
    element = element.parentNode;
  }
  
  return '/' + parts.join('/');
};

// 호버 효과 추가 (툴팁 없이)
const addHoverEffect = (element) => {
  element.classList.add('element-hover');
};

// 호버 효과 제거
const removeHoverEffect = (element) => {
  element.classList.remove('element-hover');
};

// 요소 하이라이트
const highlightElement = (element, doc) => {
  // 기존 하이라이트 제거
  const previousHighlight = doc.querySelector('.element-highlighted');
  if (previousHighlight) {
    previousHighlight.classList.remove('element-highlighted');
  }
  
  // 새 하이라이트 추가
  element.classList.add('element-highlighted');
  
  // 스크롤하여 요소가 보이도록
  element.scrollIntoView({ 
    behavior: 'smooth', 
    block: 'center' 
  });
};


// 선택된 요소에서 데이터 추출
export const extractElementData = (elementInfo) => {
  const { element, editType, section } = elementInfo;
  
  switch (editType) {
    case EDITABLE_ELEMENT_TYPES.VOCABULARY:
      return extractVocabularyData(element);
      
    case EDITABLE_ELEMENT_TYPES.QUESTION:
      return extractQuestionData(element);
      
    case EDITABLE_ELEMENT_TYPES.PASSAGE:
      return extractPassageData(element);
      
    case EDITABLE_ELEMENT_TYPES.AUDIO:
      return extractAudioData(element);
      
    case EDITABLE_ELEMENT_TYPES.STYLE:
      return extractStyleData(element, elementInfo);
      
    default:
      return {
        type: editType,
        section: section,
        content: element.textContent?.trim() || '',
        html: element.outerHTML
      };
  }
};

// 단어 데이터 추출
const extractVocabularyData = (element) => {
  const card = element.closest('.flashcard, .word-card, [data-word]');
  
  return {
    type: 'vocabulary',
    word: card?.querySelector('.word, .front, [data-word]')?.textContent?.trim() || '',
    meaning: card?.querySelector('.meaning, .back, [data-meaning]')?.textContent?.trim() || '',
    example: card?.querySelector('.example, [data-example]')?.textContent?.trim() || '',
    element: card || element
  };
};

// 문제 데이터 추출
const extractQuestionData = (element) => {
  const question = element.closest('.question, .quiz-item, [data-question]');
  
  return {
    type: 'question',
    question: question?.querySelector('.question-text, h3, p')?.textContent?.trim() || '',
    choices: Array.from(question?.querySelectorAll('.choice, .option') || []).map(choice => 
      choice.textContent?.trim()
    ),
    answer: question?.querySelector('[data-answer]')?.dataset.answer || '',
    element: question || element
  };
};

// 지문 데이터 추출
const extractPassageData = (element) => {
  return {
    type: 'passage',
    title: element.closest('body')?.querySelector('.passage-title, h1, h2')?.textContent?.trim() || '',
    content: element.closest('.passage, .reading-content')?.textContent?.trim() || 
             element.textContent?.trim() || '',
    element: element
  };
};

// 오디오 데이터 추출
const extractAudioData = (element) => {
  const audio = element.closest('audio, [data-audio]') || element;
  
  return {
    type: 'audio',
    src: audio.src || audio.dataset?.audio || '',
    script: audio.dataset?.script || '',
    title: audio.dataset?.title || '',
    element: audio
  };
};

// 스타일 데이터 추출
const extractStyleData = (element, elementInfo) => {
  const section = elementInfo.section || 'design-settings';
  
  return {
    type: 'style',
    section: section, // 'background', 'card', 'font', 'cardsize' 또는 'design-settings'
    styleSection: elementInfo.styleSection, // 'style-background' 등
    content: element.textContent?.trim() || '',
    element: element
  };
};


// 학생 정보 입력 자동 스킵
const autoSkipStudentInput = (doc) => {
  try {
    console.log('🔄 학생 정보 입력 스킵 시도...');

    // 더 강력하고 포괄적인 스킵 방법들
    const skipMethods = [
      // 방법 1: 이름 입력 화면 완전히 숨기고 학습 화면 강제 표시
      () => {
        // 이름 입력 화면 찾기 (더 포괄적인 셀렉터)
        const nameScreens = doc.querySelectorAll('#nameScreen, #nameInputSection, .name-screen, .name-input-section, .student-input, .name-input, [id*="name"], [class*="name-input"], .container > div:first-child');
        
        // 학습 화면 찾기 (더 포괄적인 셀렉터)
        const learningScreens = doc.querySelectorAll('#learningScreen, #mainContent, .learning-screen, .main-content, .quiz-screen, .flashcard-screen, .quiz-content, .learning-content, [id*="learning"], [class*="learning"], .container > div:not(:first-child)');
        
        let success = false;
        
        // 이름 입력 화면들 완전히 숨김
        nameScreens.forEach(screen => {
          if (screen && screen.style.display !== 'none') {
            screen.style.display = 'none !important';
            screen.style.visibility = 'hidden';
            screen.style.opacity = '0';
            screen.style.height = '0px';
            screen.style.overflow = 'hidden';
            screen.setAttribute('hidden', 'true');
            screen.classList.add('d-none');
            success = true;
            console.log('✅ 이름 입력 화면 숨김:', screen.className || screen.id);
          }
        });
        
        // 학습 화면들 강제 표시
        learningScreens.forEach(screen => {
          if (screen && (screen.style.display === 'none' || screen.classList.contains('hidden') || screen.hasAttribute('hidden'))) {
            screen.style.display = 'block !important';
            screen.style.visibility = 'visible';
            screen.style.opacity = '1';
            screen.style.height = 'auto';
            screen.classList.remove('hidden', 'd-none');
            screen.classList.add('show', 'active');
            screen.removeAttribute('hidden');
            success = true;
            console.log('✅ 학습 화면 표시:', screen.className || screen.id);
          }
        });
        
        return success;
      },

      // 방법 2: 이름 입력 후 시작 버튼 자동 클릭 + 검증 우회
      () => {
        const nameInputs = doc.querySelectorAll('#learnerName, #studentName, input[placeholder*="이름"], input[placeholder*="name"], input[type="text"], .name-input input, .student-input input');
        const startButtons = doc.querySelectorAll('.start-btn, #startBtn, button[onclick*="start"], button[onclick*="Start"], .btn-primary, .btn');
        
        let nameInput = null;
        let startButton = null;
        
        // 이름 입력 필드 찾기
        for (const input of nameInputs) {
          if (input.offsetParent !== null || input.id === 'learnerName') { // visible하거나 특정 ID인 input
            nameInput = input;
            break;
          }
        }
        
        // 시작 버튼 찾기 (텍스트 기반)
        for (const btn of startButtons) {
          const text = btn.textContent.trim();
          if (text.includes('시작') || text.includes('Start') || text.includes('시작하기') || text.includes('학습 시작')) {
            startButton = btn;
            break;
          }
        }
        
        if (nameInput) {
          // 이름 입력 (빈 값이어도 검증 우회를 위해 공백이라도 입력)
          nameInput.value = nameInput.value.trim() || 'AI편집모드';
          nameInput.dispatchEvent(new Event('input', { bubbles: true }));
          nameInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // alert 함수를 임시로 무력화
          const originalAlert = doc.defaultView?.alert || window.alert;
          if (doc.defaultView) {
            doc.defaultView.alert = () => {}; // alert 무시
          }
          if (window) {
            window.alert = () => {}; // alert 무시
          }
          
          if (startButton) {
            setTimeout(() => {
              startButton.click();
              startButton.dispatchEvent(new Event('click', { bubbles: true }));
              console.log('✅ 자동으로 학습 시작 (이름 입력 + 시작 버튼 + alert 우회)');
              
              // 1초 후 alert 함수 복원
              setTimeout(() => {
                if (doc.defaultView) {
                  doc.defaultView.alert = originalAlert;
                }
                if (window && originalAlert) {
                  window.alert = originalAlert;
                }
              }, 1000);
            }, 50);
          }
          return true;
        }
        return false;
      },

      // 방법 3: JavaScript 시작 함수들 직접 호출 + 이름 검증 우회
      () => {
        const iframeWindow = doc.defaultView || doc.parentWindow || window;
        if (iframeWindow) {
          // 이름 입력 필드에 값 설정 (함수 호출 전에)
          const nameInput = doc.querySelector('#learnerName, #studentName, input[placeholder*="이름"]');
          if (nameInput && !nameInput.value.trim()) {
            nameInput.value = 'AI편집모드';
          }
          
          // alert 함수 임시 무력화
          const originalAlert = iframeWindow.alert;
          iframeWindow.alert = () => {};
          
          const startFunctions = [
            'startFlashcardLearning', 'startLearning', 'startQuiz', 'startGame', 'begin', 
            'init', 'initialize', 'startCareerAnalysis', 'showLearningScreen', 'hideName',
            'showMainContent', 'skipNameInput', 'goToLearning'
          ];
          
          for (const funcName of startFunctions) {
            try {
              if (typeof iframeWindow[funcName] === 'function') {
                // 전역 변수 설정 (일부 템플릿에서 사용할 수 있음)
                if (iframeWindow.learnerName !== undefined) {
                  iframeWindow.learnerName = 'AI편집모드';
                }
                
                iframeWindow[funcName]();
                console.log(`✅ ${funcName}() 함수 호출 성공 (이름 검증 우회)`);
                
                // alert 함수 복원
                setTimeout(() => {
                  iframeWindow.alert = originalAlert;
                }, 1000);
                
                return true;
              }
            } catch (error) {
              console.log(`❌ ${funcName}() 호출 실패:`, error);
            }
          }
          
          // alert 함수 복원 (실패한 경우에도)
          iframeWindow.alert = originalAlert;
        }
        return false;
      },

      // 방법 4: CSS 스타일 강제 적용
      () => {
        let styleElement = doc.getElementById('auto-skip-style');
        if (!styleElement) {
          styleElement = doc.createElement('style');
          styleElement.id = 'auto-skip-style';
          styleElement.textContent = `
            #nameScreen, .name-screen, .name-input-section, .student-input, .name-input {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
            }
            
            #learningScreen, .learning-screen, .main-content, .quiz-screen, .flashcard-screen, 
            .quiz-content, .learning-content, .container > div:not(:first-child) {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            
            .hidden, .d-none {
              display: block !important;
            }
          `;
          
          if (doc.head) {
            doc.head.appendChild(styleElement);
            console.log('✅ CSS 강제 스타일 적용');
            return true;
          } else if (doc.body) {
            doc.body.appendChild(styleElement);
            console.log('✅ CSS 강제 스타일 적용 (body에)');
            return true;
          }
        }
        return false;
      },

      // 방법 5: DOM 구조 직접 조작
      () => {
        let success = false;
        
        // 모든 hidden 클래스/속성 제거
        const hiddenElements = doc.querySelectorAll('.hidden, .d-none, [hidden]');
        hiddenElements.forEach(el => {
          el.classList.remove('hidden', 'd-none');
          el.removeAttribute('hidden');
          el.style.display = 'block';
          el.style.visibility = 'visible';
          el.style.opacity = '1';
          success = true;
        });
        
        // 학습 관련 요소들 모두 표시 (디자인 섹션 제외)
        const learningElements = doc.querySelectorAll('[id*="learning"], [class*="learning"], [id*="quiz"], [class*="quiz"], .word-card, .flashcard, [id*="main"], [class*="main"]');
        learningElements.forEach(el => {
          if (el.style.display === 'none' || el.style.display === '') {
            el.style.display = 'block';
            el.style.visibility = 'visible';
            el.style.opacity = '1';
            success = true;
          }
        });
        
        if (success) {
          console.log('✅ DOM 구조 직접 조작 완료');
        }
        return success;
      }
    ];

    // 즉시 실행 함수
    const executeSkipMethods = () => {
      console.log('🔄 스킵 방법들 실행 중...');
      let anySuccess = false;
      
      // 모든 방법을 시도 (하나가 성공해도 다른 방법들도 계속 시도해서 더 확실하게)
      for (let i = 0; i < skipMethods.length; i++) {
        try {
          if (skipMethods[i]()) {
            console.log(`✅ 방법 ${i + 1} 성공`);
            anySuccess = true;
          }
        } catch (error) {
          console.log(`❌ 방법 ${i + 1} 실패:`, error);
        }
      }
      
      return anySuccess;
    };

    // 즉시 실행
    let initialSuccess = executeSkipMethods();
    
    // 지연 실행들 (로딩 시간 차이 고려)
    setTimeout(() => {
      console.log('🔄 500ms 후 재실행...');
      executeSkipMethods();
    }, 500);
    
    setTimeout(() => {
      console.log('🔄 1초 후 재실행...');
      executeSkipMethods();
    }, 1000);
    
    setTimeout(() => {
      console.log('🔄 2초 후 재실행...');
      executeSkipMethods();
    }, 2000);

    // MutationObserver로 동적 변화 지속 감지
    const observer = new MutationObserver((mutations) => {
      let shouldRetry = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // 새로운 이름 입력 요소가 추가되었는지 확인
          const addedNodes = Array.from(mutation.addedNodes);
          for (const node of addedNodes) {
            if (node.nodeType === 1) { // Element node
              const hasNameInput = node.querySelector && (
                node.querySelector('#nameScreen') ||
                node.querySelector('.name-input') ||
                node.querySelector('input[placeholder*="이름"]') ||
                node.id === 'nameScreen' ||
                node.classList?.contains('name-input')
              );
              
              if (hasNameInput) {
                shouldRetry = true;
                break;
              }
            }
          }
        } else if (mutation.type === 'attributes') {
          // 스타일이나 클래스 변경 감지
          const target = mutation.target;
          if (target && (
            target.id === 'nameScreen' ||
            target.classList?.contains('name-input') ||
            target.classList?.contains('name-screen')
          )) {
            // display가 block으로 다시 변경되었다면 재시도
            if (target.style.display !== 'none' && !target.classList.contains('d-none') && !target.hasAttribute('hidden')) {
              shouldRetry = true;
            }
          }
        }
      });
      
      if (shouldRetry) {
        console.log('🔄 DOM 변화 감지, 즉시 재시도...');
        setTimeout(() => {
          executeSkipMethods();
        }, 50); // 아주 짧은 지연으로 즉시 실행
      }
    });

    if (doc.body) {
      observer.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
    }

    // 10초 후 observer 자동 해제 (더 오래 감시)
    setTimeout(() => {
      observer.disconnect();
      console.log('🔄 MutationObserver 감시 종료');
    }, 10000);

  } catch (error) {
    console.error('❌ 학생 정보 입력 스킵 실패:', error);
  }
};

// 정리 함수
export const cleanupElementSelector = (iframe) => {
  if (iframe._selectionCleanup) {
    iframe._selectionCleanup();
    delete iframe._selectionCleanup;
  }
};